import { useCallback, useEffect, useMemo, useState } from "react";
import Filtros from "./Filtros";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { Box, Checkbox, CircularProgress, InputAdornment, Link, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { IFilters, InputProduto } from "..";
import DialogEncomendas from "./DialogEncomendas";
import SazonalidadeChip from "./sazonalidadeChip";
import { calcularDiferencaEmMeses } from "../../../../../helpers/calcularDiferencaEmMeses";
import { useFormContext } from "react-hook-form";

export default function Step01({ onChange, filter, setFilter, show = false, selecionados = [] }: Step01Props) {

    const { setValue } = useFormContext();
    
    const modalEncomendasDefaultValue = useMemo(() => ({
        modalState: false,
        data: null
    }), [])

    const [openEncomendas, setOpenEncomendas] = useState<TOpenModalEncomendas>(modalEncomendasDefaultValue)
    const [fetchData, setFetchData] = useState(false)
    const [focusRow, setFocusRow] = useState<number>(-1)

    const handleOpenModalEncomendas = useCallback((action: "open" | "close", value?: TOpenModalEncomendas | null) => {
        if (action === "open" && value) setOpenEncomendas(value)
        if (action === "close") setOpenEncomendas(modalEncomendasDefaultValue)
    }, [modalEncomendasDefaultValue])


    const { data, isFetching } = useQuery({
        queryKey: ["pesquisar-produtos-ressuprimento", filter],
        queryFn: () => axiosClient.get("/estoque/pesquisar-produtos-ressuprimento", { params: transformFilters() }),
        refetchOnWindowFocus: false,
        enabled: fetchData,
        cacheTime: 0
    });

    const previsao: TPrevisao = data?.data;

    // Funções Manipuladoras
    const handleChange = (codigo: number, nome: string, checkbox: boolean, quantidade: number = 0, departamento: string, setor: string, sugerido: boolean) => {
        let newSelecionados: Array<InputProduto> = [...selecionados];
        const params = {
            codigo,
            nome,
            quantidade,
            encomenda: null,
            departamento,
            setor,
            nao_sugerido: false
        }

        if (!checkbox) {
            newSelecionados = newSelecionados.filter((i) => i.codigo !== codigo);
        }

        if (checkbox) {
            let index = newSelecionados.findIndex((i) => i.codigo === codigo);
            if (index < 0) {
                newSelecionados.push(params)
            } else {
                newSelecionados[index] = params
            }
        }

        onChange(newSelecionados);
    }

    const transformFilters = useCallback(() => {
        const { predSelecionado, origem, destino, ...filterFormatado } = filter;

        const transformedFilters = {
            ...filterFormatado,
            origem_loja_id: origem?.id || null,
            destino_loja_id: destino?.id || null,
            periodo_vendas_inicio: filter.periodo_vendas_inicio.format('YYYY-MM-DD'),
            periodo_vendas_fim: filter.periodo_vendas_fim.format('YYYY-MM-DD'),
            sazonalidade_inicio: filter.sazonalidade_inicio.format('YYYY-MM-DD'),
            sazonalidade_fim: filter.sazonalidade_fim.format('YYYY-MM-DD'),
            departamentos: filter.departamentos?.map(i => (i.id))
        };

        return transformedFilters;
    }, [filter]);

    const handleFocus = (rowIndex: number) => {
        setFocusRow(rowIndex);
    };

    function extrairNomeLoja(nome: string) {
        if (nome.includes('-')) {
            return nome.split('-')[1].trim();
        } else if (nome.includes(' ')) {
            return nome.split(' ')[1].trim();
        } else {
            return '';
        }
    }
    //

    useEffect(() => {
        const filledFields = filter.destino && filter.origem ? true : false
        if (filledFields) setFetchData(true)
    }, [filter.destino, filter.origem])

    return (
        <Box display={show ? 'block' : 'none'}>
            <Filtros
                values={filter}
                onChange={(values: any) => {
                    setFilter(prevState => ({ ...prevState, ...values, limit: 50, page: 0 }));
                    if (values.origem !== undefined) setValue('filtros.origem', values.origem);
                    if (values.destino !== undefined) setValue('filtros.destino', values.destino);
                }}
            />

            {
                isFetching &&
                <Box
                    display="flex"
                    justifyContent="center"
                    paddingY={3}
                >
                    <CircularProgress sx={{ margin: 1, marginTop: 2 }} />
                </Box>
            }

            {
                (previsao && !isFetching) &&
                (
                    <>

                        <Table size="small" sx={{ marginTop: 2 }}>
                            <TableHead sx={{ position: 'sticky', top: 40, backgroundColor: 'white', zIndex: 100 }}>
                                <TableRow>
                                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>
                                        <div style={{ marginTop: 8 }}>Produto</div>
                                        <Typography variant="caption">Departamento / Setor</Typography>
                                    </TableCell>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', border: 0 }}>Qtd. em estoque</TableCell>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', border: 0 }}>Qtd. total de vendas</TableCell>
                                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }} width={150} align='center'>Estoque (dias)</TableCell>
                                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }} align='center'>Sugestão para {filter.qtdSuggestion} dia(s)</TableCell>
                                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }} width={150} align='center'>Solicitar</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Typography component="div" variant="caption">Origem</Typography>
                                        <Typography component="div" variant="caption">{extrairNomeLoja(filter.origem.nome)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography component="div" variant="caption">Destino</Typography>
                                        <Typography component="div" variant="caption">{extrairNomeLoja(filter.destino.nome)}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 90 }}>
                                        <Typography component="div" variant="caption">Período</Typography>
                                        <Typography component="div" variant="caption">{calcularDiferencaEmMeses(transformFilters().periodo_vendas_inicio, transformFilters().periodo_vendas_fim)} meses</Typography>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 82 }}>
                                        <Typography component="div" variant="caption">Sazonal</Typography>
                                        <Typography component="div" variant="caption">{calcularDiferencaEmMeses(transformFilters().sazonalidade_inicio, transformFilters().sazonalidade_fim)} meses</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    previsao?.data.map((row: TProdutosRessuprimento, index: number) => {
                                        const existeRecomendacao = !Boolean(row.nao_recomendado)
                                        let selecionado = selecionados.find(({ codigo }) => codigo === row.codigo);
                                        let encomendasProp: TOpenModalEncomendas = modalEncomendasDefaultValue;
                                        let qtdVendasDiaArredondado = Math.ceil(row.qtd_vendas_media_diaria * 100) / 100;
                                        let qtdVendasSazonalDiaArredondado = Math.ceil(row.qtd_vendas_sazonal_media_diaria * 100) / 100;

                                        if (selecionado && selecionado.quantidade > 0) {
                                            encomendasProp = {
                                                modalState: true,
                                                data: {
                                                    ...selecionado,
                                                },
                                            };
                                        }

                                        let nivelColor = "#ccc"
                                        if (row.nivel >= 0 && row.nivel <= 15) nivelColor = '#ff8a8a'
                                        if (row.nivel > 15 && row.nivel <= 30) nivelColor = '#FEC1C1'
                                        if (row.nivel > 30 && row.nivel <= 60) nivelColor = '#f9e785'
                                        if (row.nivel > 60) nivelColor = '#E1FEE0'

                                        return (
                                            <TableRow key={row.codigo} sx={{ backgroundColor: focusRow === index ? 'rgb(237, 247, 237)' : 'none' }}>
                                                <TableCell>{row.codigo}</TableCell>
                                                <TableCell>
                                                    <Typography>{row.nome}</Typography>
                                                    <Typography variant="caption">{row.departamento} / {row.setor}</Typography>
                                                    <Stack direction="row" spacing={0.2}>
                                                        {
                                                            row.sazonalidades.map((item: any, index) =>
                                                            (
                                                                <SazonalidadeChip key={index} nome={item.nome} cores={item.cores} />
                                                            ))
                                                        }
                                                    </Stack>
                                                </TableCell>

                                                <TableCell>{row.estoque_origem}</TableCell>

                                                <TableCell>{row.estoque_atual}</TableCell>

                                                <TableCell>
                                                    <Tooltip title={`${qtdVendasDiaArredondado} por dia`}>
                                                        <Box>{row.qtd_vendas}</Box>
                                                    </Tooltip>
                                                </TableCell>

                                                <TableCell>
                                                    <Tooltip title={`${qtdVendasSazonalDiaArredondado} por dia`}>
                                                        <Box>{row.qtd_vendas_sazonal}</Box>
                                                    </Tooltip>
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    sx={{ backgroundColor: nivelColor }}>
                                                    <Typography variant="body2">{existeRecomendacao ? `${row.nivel} dia(s)` : "-"}</Typography>
                                                </TableCell>

                                                <TableCell align="center">
                                                    {
                                                        existeRecomendacao ?
                                                            Math.ceil((
                                                                qtdVendasDiaArredondado > qtdVendasSazonalDiaArredondado ? qtdVendasDiaArredondado : qtdVendasSazonalDiaArredondado)
                                                                * filter.qtdSuggestion
                                                            ) : "Não sugerido"
                                                    }
                                                </TableCell>

                                                <TableCell>
                                                    <div style={{ display: 'flex' }}>
                                                        <Checkbox
                                                            checked={selecionado ? true : false}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                handleChange(row.codigo, row.nome, event.target.checked, 0, row.departamento, row.setor, row.sugerido);
                                                            }}
                                                        />
                                                        <Box display={'flex'} flexDirection={'column'}>
                                                            <TextField
                                                                size="small"
                                                                variant="outlined"
                                                                error={selecionado && selecionado.quantidade <= 0 ? true : false}
                                                                value={selecionado?.quantidade || ''}
                                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                    handleChange(row.codigo, row.nome, true, parseInt(event.target.value) || 0, row.departamento, row.setor, row.sugerido);
                                                                }}
                                                                onFocus={() => handleFocus(index)}
                                                                onBlur={() => handleFocus(-1)}
                                                                slotProps={{
                                                                    htmlInput: { tabIndex: index + 1 },
                                                                    inputLabel: { shrink: selecionado ? true : false },
                                                                    input: { endAdornment: <InputAdornment position="end">{row.unidade_medida}</InputAdornment>, }
                                                                }}
                                                                sx={{
                                                                    minWidth: 100
                                                                }}
                                                            />
                                                            {
                                                                selecionado && !selecionado.encomenda &&

                                                                <Box marginTop={1} width={'100%'} display={'flex'} justifyContent={'flex-end'}>
                                                                    <Link component="button" fontSize={12} textAlign={'right'} justifyContent={"flex-end"}
                                                                        onClick={() => handleOpenModalEncomendas('open', encomendasProp ? encomendasProp : null)}
                                                                    >É encomenda?
                                                                    </Link>
                                                                </Box>

                                                            }
                                                            {
                                                                selecionado && selecionado.encomenda &&

                                                                <Box marginTop={1} width={'100%'}>
                                                                    <Link width={'100%'} component="button" fontSize={11} textAlign={'right'} justifyContent={"flex-end"}
                                                                        onClick={() => handleOpenModalEncomendas('open', encomendasProp ? encomendasProp : null)}
                                                                    >Encomenda: {selecionado.encomenda} {row.unidade_medida}
                                                                    </Link>
                                                                </Box>

                                                            }
                                                            {
                                                                openEncomendas.modalState && openEncomendas.data?.codigo === selecionado?.codigo &&
                                                                <DialogEncomendas
                                                                    {...{ selecionados, onChange }}
                                                                    open={openEncomendas.modalState}
                                                                    row={row}
                                                                    selecionado={openEncomendas.data}
                                                                    handleClose={() => handleOpenModalEncomendas("close")}
                                                                />
                                                            }
                                                        </Box>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                }

                                {
                                    previsao?.total === 0 &&
                                    <TableRow>
                                        <TableCell colSpan={9}>Nenhum item para exibir</TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={previsao?.total}
                            page={filter.page}
                            rowsPerPage={filter.limit}
                            onPageChange={(
                                event: React.MouseEvent<HTMLButtonElement> | null,
                                newPage: number
                            ) => {
                                setFilter({ ...filter, page: newPage })
                            }}
                            onRowsPerPageChange={(
                                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                            ) => {
                                setFilter({ ...filter, page: 0, limit: parseInt(event.target.value, 10) })
                            }}
                        />

                    </>
                )
            }

        </Box>
    )
}

interface Step01Props {
    show: boolean,
    selecionados: Array<InputProduto>;
    onChange: (produtos: Array<InputProduto>) => void;
    filter: IFilters
    setFilter: React.Dispatch<React.SetStateAction<IFilters>>
}
export type TOpenModalEncomendas = {
    modalState: boolean,
    data: InputProduto | null
}

type TPrevisao = {
    total: number;
    data: Array<TProdutosRessuprimento>;
    per_page: number;
    current_page: number;
}

export type TProdutosRessuprimento = {
    codigo: number;
    departamento: string;
    departamento_id: string;
    estoque_atual: number;
    estoque_origem: number;
    nivel: number;
    nome: string;
    qtd_vendas: number;
    qtd_vendas_dias_suporta: number;
    qtd_vendas_media_diaria: number;
    qtd_vendas_media_mensal: number;
    qtd_vendas_sazonal: number;
    qtd_vendas_sazonal_dias_suporta: number | null;
    qtd_vendas_sazonal_media_diaria: number;
    qtd_vendas_sazonal_media_mensal: number;
    sazonalidades: [{ nome: string, cores: string, inicio: string, fim: string }];
    setor: string;
    setor_id: string;
    sugerido: boolean;
    unidade_medida: string;
    nao_recomendado: number;
}